import { ApiAxiosInstance } from '@/api/instances/apiAxiosInstance';
import type { TypeToken } from '@/types/Login';
import { session } from '@/helpers/auth';
import { getTokenTimeRemaining } from '@/shared/common/utils/jwt.util';
import { logger } from './logger.util';
import {
  REFRESH_TOKEN_KEY,
  TOKEN_EXPIRES_AT_KEY,
  AUTH_TOKEN_KEY,
  LAST_ACTIVITY_TIMESTAMP_KEY,
  LAST_REFRESH_ATTEMPT_KEY,
  REFRESH_COORDINATION_WINDOW_MS,
  REFRESH_THRESHOLD_SECONDS,
} from '../constants/auth.constant';

/**
 * Obtiene el timestamp de expiración guardado en localStorage.
 * @returns Timestamp de expiración en milisegundos o null si no existe
 */
export function getTokenExpirationTimestamp(): number | null {
  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_AT_KEY);
  if (!expiresAt) {
    return null;
  }

  const timestamp = Number.parseInt(expiresAt, 10);
  return Number.isNaN(timestamp) ? null : timestamp;
}

/**
 * Calcula y guarda el timestamp de expiración basado en expiresIn.
 * @param expiresIn - Tiempo de expiración en segundos
 */
export function saveTokenExpiration(expiresIn: number): void {
  if (expiresIn && expiresIn > 0) {
    const expirationTimestamp = Date.now() + expiresIn * 1000;
    localStorage.setItem(TOKEN_EXPIRES_AT_KEY, expirationTimestamp.toString());
  }
}

/**
 * Guarda el refreshToken en localStorage.
 * @param refreshToken - Token de refresco a guardar
 */
export function saveRefreshToken(refreshToken: string): void {
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
}

/**
 * Obtiene el refreshToken de localStorage.
 * @returns Token de refresco o null si no existe
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Actualiza los tokens de autenticación y los guarda en localStorage.
 *
 * Sin un `idToken` usable no persiste nada y lo registra como error: es el único
 * dato que decide si la sesión queda iniciada, y el llamador no distingue el no-op
 * de un éxito. El `'null'` literal llega como string desde el backend.
 *
 * @param tokens - Objeto con los tokens a actualizar
 */
export function updateTokens({
  idToken,
  refreshToken,
  expiresIn,
}: Partial<TypeToken> & { expiresIn?: number }): void {
  if (!idToken || idToken === 'null') {
    logger.error('No se persistió la sesión: idToken no usable', {
      idTokenPresente: Boolean(idToken),
      idTokenEsStringNull: idToken === 'null',
      refreshTokenPresente: Boolean(refreshToken),
      expiresInPresente: Boolean(expiresIn),
    });
    return;
  }

  const instances = [ApiAxiosInstance];

  instances.forEach((instance) => {
    if (instance) {
      instance.defaults.headers.Authorization = idToken;
    }
  });

  localStorage.setItem(AUTH_TOKEN_KEY, idToken);

  if (refreshToken) {
    saveRefreshToken(refreshToken);
  }

  if (expiresIn) {
    saveTokenExpiration(expiresIn);
  }
}

/**
 * Obtiene el timestamp de la última actividad compartida entre pestañas.
 * @param localTimestamp - Timestamp local de última actividad
 * @returns El timestamp más reciente entre local y compartido, o el tiempo actual si no existe
 */
export function getSharedLastActivityTime(localTimestamp: number): number {
  const sharedTimestampStr = localStorage.getItem(LAST_ACTIVITY_TIMESTAMP_KEY);
  if (!sharedTimestampStr) {
    return localTimestamp;
  }

  const sharedTimestamp = Number.parseInt(sharedTimestampStr, 10);
  if (Number.isNaN(sharedTimestamp)) {
    return localTimestamp;
  }

  return Math.max(localTimestamp, sharedTimestamp);
}

/**
 * Obtiene el timestamp del último intento de refresco desde localStorage.
 * @returns Timestamp en milisegundos o null si no existe o ha expirado
 */
export function getLastRefreshAttempt(): number | null {
  const attemptStr = localStorage.getItem(LAST_REFRESH_ATTEMPT_KEY);
  if (!attemptStr) {
    return null;
  }

  const attemptTimestamp = Number.parseInt(attemptStr, 10);
  if (Number.isNaN(attemptTimestamp)) {
    return null;
  }

  const age = Date.now() - attemptTimestamp;
  if (age > REFRESH_COORDINATION_WINDOW_MS) {
    localStorage.removeItem(LAST_REFRESH_ATTEMPT_KEY);
    return null;
  }

  return attemptTimestamp;
}

/**
 * Establece el timestamp del último intento de refresco en localStorage.
 */
export function setLastRefreshAttempt(): void {
  localStorage.setItem(LAST_REFRESH_ATTEMPT_KEY, Date.now().toString());
}

/**
 * Elimina el timestamp del último intento de refresco de localStorage.
 */
export function clearLastRefreshAttempt(): void {
  localStorage.removeItem(LAST_REFRESH_ATTEMPT_KEY);
}

/**
 * Calcula el tiempo restante del token en segundos.
 * @returns Tiempo restante en segundos o 0 si ha expirado
 */
export function getRemainingTime(): number {
  const expirationTimestamp = getTokenExpirationTimestamp();
  if (expirationTimestamp) {
    const remaining = Math.floor((expirationTimestamp - Date.now()) / 1000);
    return Math.max(remaining, 0);
  }

  const token = session();
  if (token) {
    const remaining = getTokenTimeRemaining(token);
    return remaining;
  }

  return 0;
}

/**
 * Verifica si el token necesita ser refrescado.
 * @returns true si necesita ser refrescado, false en caso contrario
 */
export function shouldRefreshToken(): boolean {
  const remaining = getRemainingTime();
  return remaining > 0 && remaining <= REFRESH_THRESHOLD_SECONDS;
}
